/**
 * Metric name <-> storage key codec.
 *
 * Detection metrics are conventionally written PASCAL VOC / COCO style with the IoU threshold after
 * an `@` (`AP@0.5`, `mAP@[0.5:0.95]`). Those characters cannot live in a metrics-object key, so the
 * threshold is encoded the way COCO abbreviates it (AP50, AP75): as a two-digit percentage.
 *
 *   AP@0.5          <-> ap_50
 *   mAP@[0.5:0.95]  <-> map_50_95
 *   Recall@10       <-> recall_at_10   (a count, not a threshold)
 *
 * Names without an `@` slug exactly as before (`F1 Score` -> `f1_score`).
 */

/** Tokens whose conventional casing is not plain Title Case. */
const TOKEN_CASING: Record<string, string> = {
  ap: 'AP',
  map: 'mAP',
  ar: 'AR',
  iou: 'IoU',
  giou: 'GIoU',
  diou: 'DIoU',
  ciou: 'CIoU',
  auc: 'AUC',
  roc: 'ROC',
  auroc: 'AUROC',
  qwk: 'QWK',
  npv: 'NPV',
  ppv: 'PPV',
  fnr: 'FNR',
  fpr: 'FPR',
  tpr: 'TPR',
  tnr: 'TNR',
  fps: 'FPS',
  gmacs: 'GMACs',
};

const ENCODED_THRESHOLD = '(\\d{2}|100)';
const RANGE_KEY = new RegExp(`^(.+)_${ENCODED_THRESHOLD}_${ENCODED_THRESHOLD}$`);
const SINGLE_KEY = new RegExp(`^(.+)_${ENCODED_THRESHOLD}$`);
const COUNT_KEY = /^(.+)_at_(\d+)$/;

/** `0.5` -> `50`, `0.05` -> `05`; null when the value is not a whole-percent threshold in (0, 1]. */
function encodeThreshold(raw: string): string | null {
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0 || value > 1) {
    return null;
  }
  const percent = Math.round(value * 100);
  if (Math.abs(percent - value * 100) > 1e-9) {
    return null;
  }
  return String(percent).padStart(2, '0');
}

function decodeThreshold(encoded: string): string {
  return String(Number(encoded) / 100);
}

function slug(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Storage key for a metric name; an empty string when the name has no usable characters. */
export function metricNameToKey(name: string): string {
  const encoded = String(name ?? '')
    // @[0.5:0.95] or @[0.5:0.95:0.05] -- the step is implied by the convention and dropped.
    .replace(/@\s*\[\s*([\d.]+)\s*:\s*([\d.]+)(?:\s*:\s*[\d.]+)?\s*\]/g, (match, from: string, to: string) => {
      const a = encodeThreshold(from);
      const b = encodeThreshold(to);
      return a && b ? `_${a}_${b}` : match;
    })
    .replace(/@\s*(\d*\.?\d+)/g, (match, value: string) => {
      const threshold = encodeThreshold(value);
      if (threshold) {
        return `_${threshold}`;
      }
      return /^\d+$/.test(value) ? `_at_${value}` : match;
    });
  return slug(encoded);
}

function prettifyTokens(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map((token) => TOKEN_CASING[token] ?? token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
}

/** Readable label for a storage key, restoring the `@threshold` notation `metricNameToKey` encoded. */
export function metricKeyToLabel(key: string): string {
  const value = String(key ?? '').trim();
  const range = RANGE_KEY.exec(value);
  if (range) {
    return `${prettifyTokens(range[1])}@[${decodeThreshold(range[2])}:${decodeThreshold(range[3])}]`;
  }
  const count = COUNT_KEY.exec(value);
  if (count) {
    return `${prettifyTokens(count[1])}@${count[2]}`;
  }
  const single = SINGLE_KEY.exec(value);
  if (single) {
    return `${prettifyTokens(single[1])}@${decodeThreshold(single[2])}`;
  }
  return prettifyTokens(value);
}
